import { useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import AppHeader from '../components/AppHeader'
import ProfileDropdown from '../components/ProfileDropdown'
import { 
  Cog6ToothIcon,
  ServerIcon,
  ShieldCheckIcon,
  BellIcon,
  DocumentTextIcon,
  UserGroupIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'

interface SystemSettings {
  siteName: string
  siteDescription: string
  maintenanceMode: boolean
  registrationEnabled: boolean
  emailNotifications: boolean
  smsNotifications: boolean
  maxFileSize: number
  sessionTimeout: number
  passwordMinLength: number
  requireEmailVerification: boolean
  allowGuestAccess: boolean
  defaultTimezone: string
  defaultCurrency: string
  backupFrequency: string
  logRetentionDays: number
}

const AdminSystemSettingsPage = () => {
  const navigate = useNavigate()
  const [settings, setSettings] = useState<SystemSettings>({
    siteName: 'ShootSuite',
    siteDescription: 'Professional photography management platform',
    maintenanceMode: false,
    registrationEnabled: true,
    emailNotifications: true,
    smsNotifications: false,
    maxFileSize: 50, // MB
    sessionTimeout: 60, // minutes
    passwordMinLength: 8,
    requireEmailVerification: true,
    allowGuestAccess: false,
    defaultTimezone: 'America/New_York',
    defaultCurrency: 'USD',
    backupFrequency: 'daily',
    logRetentionDays: 30
  })
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text })
    setTimeout(() => setMessage(null), 5000)
  }

  const handleSave = async () => {
    setLoading(true)
    try {
      // In a real implementation, this would save to the database
      await new Promise(resolve => setTimeout(resolve, 1000)) // Simulate API call
      showMessage('success', 'System settings updated successfully!')
    } catch (error) {
      showMessage('error', 'Failed to update system settings')
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all settings to defaults?')) {
      setSettings({
        siteName: 'ShootSuite',
        siteDescription: 'Professional photography management platform',
        maintenanceMode: false,
        registrationEnabled: true,
        emailNotifications: true,
        smsNotifications: false,
        maxFileSize: 50,
        sessionTimeout: 60,
        passwordMinLength: 8,
        requireEmailVerification: true,
        allowGuestAccess: false,
        defaultTimezone: 'America/New_York',
        defaultCurrency: 'USD',
        backupFrequency: 'daily',
        logRetentionDays: 30
      })
      showMessage('success', 'Settings reset to defaults')
    }
  }

  const settingSections = [
    {
      title: 'General Settings',
      icon: Cog6ToothIcon,
      color: 'text-blue-400',
      settings: [
        {
          key: 'siteName',
          label: 'Site Name',
          type: 'text',
          description: 'The name of your platform'
        },
        {
          key: 'siteDescription',
          label: 'Site Description',
          type: 'textarea',
          description: 'Brief description of your platform'
        },
        {
          key: 'defaultTimezone',
          label: 'Default Timezone',
          type: 'select',
          options: [
            'America/New_York',
            'America/Chicago',
            'America/Denver',
            'America/Los_Angeles',
            'Europe/London',
            'Europe/Paris',
            'Asia/Tokyo'
          ],
          description: 'Default timezone for new users'
        },
        {
          key: 'defaultCurrency',
          label: 'Default Currency',
          type: 'select',
          options: ['USD', 'EUR', 'GBP', 'CAD', 'AUD'],
          description: 'Default currency for invoices'
        }
      ]
    },
    {
      title: 'Security Settings',
      icon: ShieldCheckIcon,
      color: 'text-red-400',
      settings: [
        {
          key: 'passwordMinLength',
          label: 'Minimum Password Length',
          type: 'number',
          min: 6,
          max: 20,
          description: 'Minimum required password length'
        },
        {
          key: 'sessionTimeout',
          label: 'Session Timeout (minutes)',
          type: 'number',
          min: 15,
          max: 480,
          description: 'How long users stay logged in'
        },
        {
          key: 'requireEmailVerification',
          label: 'Require Email Verification',
          type: 'toggle',
          description: 'Users must verify email before accessing the platform'
        }
      ]
    },
    {
      title: 'User Management',
      icon: UserGroupIcon,
      color: 'text-green-400',
      settings: [
        {
          key: 'registrationEnabled',
          label: 'Allow New Registrations',
          type: 'toggle',
          description: 'Allow new users to register accounts'
        },
        {
          key: 'allowGuestAccess',
          label: 'Allow Guest Access',
          type: 'toggle',
          description: 'Allow limited access without registration'
        }
      ]
    },
    {
      title: 'Notifications',
      icon: BellIcon,
      color: 'text-purple-400',
      settings: [
        {
          key: 'emailNotifications',
          label: 'Email Notifications',
          type: 'toggle',
          description: 'Enable system email notifications'
        },
        {
          key: 'smsNotifications',
          label: 'SMS Notifications',
          type: 'toggle',
          description: 'Enable SMS notifications (requires SMS provider)'
        }
      ]
    },
    {
      title: 'File Management',
      icon: DocumentTextIcon,
      color: 'text-orange-400',
      settings: [
        {
          key: 'maxFileSize',
          label: 'Max File Size (MB)',
          type: 'number',
          min: 1,
          max: 500,
          description: 'Maximum file upload size'
        }
      ]
    },
    {
      title: 'System Maintenance',
      icon: ServerIcon,
      color: 'text-yellow-400',
      settings: [
        {
          key: 'maintenanceMode',
          label: 'Maintenance Mode',
          type: 'toggle',
          description: 'Put the system in maintenance mode'
        },
        {
          key: 'backupFrequency',
          label: 'Backup Frequency',
          type: 'select',
          options: ['hourly', 'daily', 'weekly'],
          description: 'How often to backup the database'
        },
        {
          key: 'logRetentionDays',
          label: 'Log Retention (days)',
          type: 'number',
          min: 1,
          max: 365,
          description: 'How long to keep system logs'
        }
      ]
    }
  ]

  const renderSetting = (setting: any) => {
    const value = settings[setting.key as keyof SystemSettings]

    switch (setting.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value as string}
            onChange={(e) => setSettings(prev => ({ ...prev, [setting.key]: e.target.value }))}
            className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all"
          />
        )
      
      case 'textarea':
        return (
          <textarea
            value={value as string}
            onChange={(e) => setSettings(prev => ({ ...prev, [setting.key]: e.target.value }))}
            rows={3}
            className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all resize-none"
          />
        )
      
      case 'number':
        return (
          <input
            type="number"
            value={value as number}
            min={setting.min}
            max={setting.max}
            onChange={(e) => setSettings(prev => ({ ...prev, [setting.key]: parseInt(e.target.value) }))}
            className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all"
          />
        )
      
      case 'select':
        return (
          <select
            value={value as string}
            onChange={(e) => setSettings(prev => ({ ...prev, [setting.key]: e.target.value }))}
            className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all"
          >
            {setting.options.map((option: string) => (
              <option key={option} value={option} className="bg-gray-800">
                {option}
              </option>
            ))}
          </select>
        )
      
      case 'toggle':
        return (
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={value as boolean}
              onChange={(e) => setSettings(prev => ({ ...prev, [setting.key]: e.target.checked }))}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        )
      
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <AppHeader title="System Settings" subtitle="Configure system-wide settings and preferences">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2 px-3 py-1 bg-red-500/20 border border-red-500/30 rounded-full">
            <div className="w-2 h-2 bg-red-400 rounded-full"></div>
            <span className="text-red-400 text-sm font-medium">Admin Panel</span>
          </div>
          <button
            onClick={() => navigate('/admin')}
            className="text-gray-400 hover:text-white transition-colors font-medium"
          >
            Admin Dashboard
          </button>
          <ProfileDropdown />
        </div>
      </AppHeader>

      {/* Message */}
      {message && (
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          className={`fixed top-20 left-1/2 transform -translate-x-1/2 z-50 px-6 py-3 rounded-lg ${
            message.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          } text-white font-medium`}
        >
          {message.text}
        </motion.div>
      )}

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">System Settings</h1>
            <p className="text-gray-400">Configure platform settings and preferences</p>
          </div>
          <div className="flex space-x-4">
            <button
              onClick={handleReset}
              className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-xl font-medium transition-all"
            >
              Reset to Defaults
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-xl font-medium transition-all disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>

        {/* Settings Sections */}
        <div className="space-y-8">
          {settingSections.map((section, sectionIndex) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: sectionIndex * 0.1 }}
              className="glass-mockup rounded-2xl p-6"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-r from-gray-500 to-slate-500 flex items-center justify-center`}>
                  <section.icon className={`w-5 h-5 ${section.color}`} />
                </div>
                <h2 className="text-xl font-bold text-white">{section.title}</h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {section.settings.map((setting, settingIndex) => (
                  <motion.div
                    key={setting.key}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: (sectionIndex * 0.1) + (settingIndex * 0.05) }}
                    className="space-y-3"
                  >
                    <div className="flex items-center justify-between">
                      <label className="text-white font-medium">{setting.label}</label>
                      {setting.type === 'toggle' && renderSetting(setting)}
                    </div>
                    {setting.type !== 'toggle' && renderSetting(setting)}
                    <p className="text-gray-400 text-sm">{setting.description}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Danger Zone */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-8 glass-mockup rounded-2xl p-6 border-2 border-red-500/30"
        >
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center">
              <ExclamationTriangleIcon className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-red-400">Danger Zone</h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-red-500/10 rounded-xl border border-red-500/30">
              <div>
                <h3 className="text-white font-medium">Clear All User Data</h3>
                <p className="text-gray-400 text-sm">Permanently delete all user data and reset the system</p>
              </div>
              <button
                onClick={() => {
                  if (window.confirm('This will permanently delete ALL user data. This action cannot be undone. Type "DELETE" to confirm.')) {
                    const confirmation = prompt('Type "DELETE" to confirm:')
                    if (confirmation === 'DELETE') {
                      showMessage('error', 'This feature is disabled for safety')
                    }
                  }
                }}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-all"
              >
                Clear Data
              </button>
            </div>

            <div className="flex items-center justify-between p-4 bg-yellow-500/10 rounded-xl border border-yellow-500/30">
              <div>
                <h3 className="text-white font-medium">Reset System</h3>
                <p className="text-gray-400 text-sm">Reset all settings to factory defaults</p>
              </div>
              <button
                onClick={() => {
                  if (window.confirm('This will reset ALL system settings to defaults. Continue?')) {
                    handleReset()
                  }
                }}
                className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-medium transition-all"
              >
                Reset System
              </button>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  )
}

export default AdminSystemSettingsPage 